import { useEffect, useState } from "react";

import { addFollower } from "@/api/requests/addFollower";
import { getIsFollowing } from "@/api/requests/getIsFollowing";
import { removeFollower } from "@/api/requests/removeFollower";
import { Action } from "@/constants/events";
import useDomainCountryCode from "@/hooks/useDomainCountryCode";
import { useEventTracker } from "@/hooks/useEventTracker";
import { useDispatch, useSelector } from "@/redux/hooks";
import { getHasFollowed } from "@/redux/selectors/dispensary";
import { getIsLoggedIn } from "@/redux/selectors/user";
import { follow, unfollow } from "@/redux/slices/dispensary";

import WeedMenuIcon from "@/components/Icons/weed_menu.svg";

import SsoBumpoutModal from "../SsoBumpoutModal";
import TrackImpression from "../TrackImpression";

const NoMenuFollowCTA: React.FC<{
  dispensaryId: number;
  retailType: string;
}> = ({ dispensaryId, retailType }) => {
  const { publishEvent } = useEventTracker();
  const [isSsoModalVisible, setIsSsoModalVisible] = useState(false);

  const isFollowing = useSelector(getHasFollowed);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const countryCode = useDomainCountryCode();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isFollowing) {
      getIsFollowing({
        countryCode,
        followeeId: dispensaryId,
        followeeType: "dispensary",
      }).then((isFollowing) => {
        if (isFollowing) {
          dispatch(follow());
        }
      });
    }
  }, []);

  const toggleFollowAction = async () => {
    if (!dispensaryId) {
      return;
    }

    const eventLabel = isFollowing
      ? "empty menu unfollow"
      : "empty menu follow";

    publishEvent({
      action: Action.click,
      category: `${retailType} menu`,
      label: eventLabel,
    });

    if (isFollowing) {
      const removeFollowerSuccess = await removeFollower({
        countryCode,
        followeeId: dispensaryId,
        followeeType: "dispensary",
      });

      if (removeFollowerSuccess) {
        dispatch(unfollow());
      }
    } else {
      const addFollowerSuccess = await addFollower({
        countryCode,
        followeeId: dispensaryId,
        followeeType: "dispensary",
      });

      if (addFollowerSuccess) {
        dispatch(follow());
      }
    }

    return !isFollowing;
  };

  const onButtonClick = () => {
    if (isLoggedIn) {
      toggleFollowAction();
    } else {
      setIsSsoModalVisible(true);
    }
  };

  return (
    <TrackImpression
      className="py-xl"
      trackingFunction={() => {
        publishEvent({
          action: Action.impression,
          category: `${retailType} menu`,
          label: "empty menu CTA",
        });
      }}
    >
      <div
        className="container border border-light-grey rounded flex justify-center p-lg"
        data-testid="no-menu-follow-container"
      >
        <div className="max-w-[488px] w-full">
          <div className="flex justify-center" data-testid="feedback-icon">
            <WeedMenuIcon />
          </div>
          <div className="self-center text-center text-md pt-sm font-bold">
            Menu coming soon
          </div>
          <div className="self-center text-center text-sm py-lg">
            {isFollowing ? (
              <div className="italic">
                You are following this store. We’ll let you know once their menu
                is available.
              </div>
            ) : (
              "Check back later or follow for future updates."
            )}
          </div>
          <div className="flex justify-center">
            {isFollowing ? (
              <button
                className="button button--text"
                onClick={() => onButtonClick()}
              >
                unfollow store
              </button>
            ) : (
              <button
                onClick={() => onButtonClick()}
                className="button button--secondary bg-white"
              >
                follow for updates
              </button>
            )}
          </div>
        </div>
      </div>
      {isSsoModalVisible && (
        <SsoBumpoutModal
          closeModal={() => setIsSsoModalVisible(false)}
          trackSignUp={() => {
            setIsSsoModalVisible(false);
          }}
        />
      )}
    </TrackImpression>
  );
};

export default NoMenuFollowCTA;
